import { DEFAULT_FORM_VALUES } from '../consts/defaultFormValues';
import type { FormValues, SectionConfig, SectionKey, SubItemKey } from '../types';

type IncludeAllOptions = {
  excludePassport?: boolean;
};

function areAllSectionsFullySelected(values: FormValues, sections: SectionConfig[]): boolean {
  return sections.every(
    (section) => values[section.key] && (section.subItems?.every((sub) => values[sub.key]) ?? true)
  );
}

export function computeIncludeAll(
  values: FormValues,
  sections: SectionConfig[],
  options: IncludeAllOptions = {}
): boolean {
  const sectionsAllOn = areAllSectionsFullySelected(values, sections);

  if (options.excludePassport) {
    return sectionsAllOn;
  }

  return sectionsAllOn && values.include_passport_files_as_separate_pdfs;
}

export function hasAnyExportFieldSelected(values: FormValues, sections: SectionConfig[]): boolean {
  if (values.include_passport_files_as_separate_pdfs) {
    return true;
  }

  return sections.some((section) => {
    if (values[section.key]) {
      return true;
    }

    return section.subItems?.some((sub) => values[sub.key]) ?? false;
  });
}

export function applyIncludeAllToggle(
  values: FormValues,
  sections: SectionConfig[],
  allOn: boolean,
  options: IncludeAllOptions = {}
): FormValues {
  const updated = { ...values };

  sections.forEach((section) => {
    updated[section.key] = allOn;
    section.subItems?.forEach((sub) => {
      updated[sub.key] = allOn;
    });
  });

  if (options.excludePassport) {
    updated.include_passport_files_as_separate_pdfs = false;
  } else {
    updated.include_passport_files_as_separate_pdfs = allOn;
  }

  return updated;
}

export function applySectionToggle(values: FormValues, sections: SectionConfig[], key: SectionKey): FormValues {
  const next = !values[key];
  const updated = { ...values, [key]: next };
  const section = sections.find((s) => s.key === key);

  section?.subItems?.forEach((sub) => {
    updated[sub.key] = next;
  });

  return updated;
}

export function applySubItemToggle(values: FormValues, sections: SectionConfig[], key: SubItemKey): FormValues {
  const nextValue = !values[key];
  const updated = { ...values, [key]: nextValue };
  const parentSection = sections.find((section) => section.subItems?.some((sub) => sub.key === key));

  if (!parentSection?.subItems) {
    return updated;
  }

  const allSubItemsOff = parentSection.subItems.every(
    (sub) => (sub.key === key ? nextValue : values[sub.key]) === false
  );
  const anySubItemOn = parentSection.subItems.some((sub) => (sub.key === key ? nextValue : values[sub.key]) === true);

  updated[parentSection.key] = allSubItemsOff ? false : anySubItemOn ? true : values[parentSection.key];

  return updated;
}

export function clearAllFormValues(): FormValues {
  return { ...DEFAULT_FORM_VALUES };
}
